import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ClipboardCheck } from 'lucide-react'
import {
  Card, CardHeader, EmptyState, ErrorState, Identifier, LoadingState,
  NoPermissionState, useLocale,
} from '@hk/ui'
import {
  ApiError, fetchInspectionQueue, formatCount, formatMinutes, formatTime, nameIn,
  pluralise, translateError, type InspectionQueueRow,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'
import { InspectRoom } from './InspectRoom'

/** The same `.replace` every screen in this folder does. */
const fill = (template: string, values: Record<string, string>) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, value), template)

/* ============================================================================
   W-50 — WHAT IS WAITING FOR AN INSPECTOR.  Sprint 65.

   ── THE HANDSET HAS A BUTTON AND THIS IS A LIST ────────────────────────────────
   M-12 sends `nextToInspect`: the longest-waiting room, so that *"Inspect next"* goes
   somewhere the moment it is pressed. That is right for a supervisor walking a corridor
   with a phone — he wants the next room, not a menu.

   **IT IS WRONG FOR THE DESK.** A manager at nine in the morning is not asking "what shall
   I inspect?" He is asking *"is this under control, and if not, where is it stuck?"* —
   which is a question about the shape of the queue. So the headline is the two numbers
   that answer it (how many, how old the oldest is), and the rows carry the two facts that
   tell him where to start: how long each has waited, and how many lines the attendant
   himself said he could not do.

   ── HIS OWN ROOMS ARE HERE, MARKED ─────────────────────────────────────────────
   A queue that quietly dropped the rooms this caller cannot inspect would tell him the
   backlog is shorter than it is, which is the number he came for. They are listed, marked
   and not openable — the server's own reason, drawn before he presses anything (#144).
   ========================================================================= */

export function InspectionQueue({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]
  const [open, setOpen] = useState<string>()

  const queue = useQuery({
    queryKey: ['inspection-queue', propertyId],
    queryFn: ({ signal }) => fetchInspectionQueue(propertyId, { signal }),
    /* A minute. The rows age by the minute and nothing else about them moves, so a
       thirty-second poll would be twice the traffic for the same screen. */
    refetchInterval: 60_000,
    retry: false,
  })

  /* BEFORE THE LOADING AND ERROR BRANCHES, for the reason the fault board gives: passing a
     room invalidates this query, which puts the list back into `isLoading` — and a screen
     that vanished under the hand that had just pressed Pass is a screen somebody presses
     twice to check. */
  if (open) {
    return (
      <InspectRoom taskId={open} propertyId={propertyId} onClose={() => setOpen(undefined)} />
    )
  }

  if (queue.isLoading && !queue.error) return <LoadingState rows={5} />

  if (queue.error && !queue.data) {
    if (queue.error instanceof ApiError && queue.error.code === 'permission.denied')
      return <NoPermissionState />
    const translated = translateError(queue.error, locale)
    return (
      <ErrorState
        body={translated.message}
        onRetry={queue.refetch}
        {...(translated.correlationId ? { correlationId: translated.correlationId } : {})}
      />
    )
  }

  const view = queue.data!

  return (
    <div className="space-y-4 p-4">
      {/* **A SENTENCE AND NOT A TILE**, the same choice W-10 made: "seven waiting, the
          oldest for forty-one minutes" is a morning, and two numbers in boxes are two
          numbers in boxes. */}
      <p className="text-sm">
        {view.waiting === 0
          ? t.iqAllClear
          : fill(
            pluralise(t.iqHeadline, view.waiting, locale, formatCount(view.waiting, locale)),
            { oldest: formatMinutes(view.oldestWaitingMinutes ?? 0, locale) })}
      </p>

      {view.blockedForYou > 0 && (
        <p className="text-sm text-muted-foreground">
          {pluralise(t.iqBlockedForYou, view.blockedForYou, locale,
            formatCount(view.blockedForYou, locale))}
        </p>
      )}

      <Card>
        <CardHeader
          title={t.iqTitle}
          icon={<ClipboardCheck className="size-4" />}
          actions={
            <span className="tnum text-[12px] text-muted-fg">
              {formatCount(view.waiting, locale)}
            </span>
          }
        />
        <div className="flex flex-col gap-1.5 p-2.5">
          {view.rows.length === 0 && <EmptyState title={t.iqEmpty} />}
          {view.rows.map((row) => (
            <Row key={row.taskId} row={row} onOpen={() => setOpen(row.taskId)} />
          ))}
        </div>
      </Card>
    </div>
  )
}

function Row({ row, onOpen }: { row: InspectionQueueRow; onOpen: () => void }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]
  const barred = row.cannotInspect !== null

  /* AN HOUR. Not a threshold anybody asked for — it is the point past which a supervisor
     would want to be told, and it is drawn rather than sorted on, because the list is
     already oldest-first and re-sorting by urgency would move rows under a reading eye. */
  const old = row.waitingMinutes >= 60

  return (
    <article className={`rounded-hk border p-2.5 ${
      old ? 'border-[#fcd34d] bg-[#fffbeb] dark:border-[#b45309] dark:bg-[#2a1e08]'
        : 'border-border bg-background'}`}
    >
      <div className="flex items-start gap-2">
        <Identifier>{row.roomNumber}</Identifier>

        <span className="min-w-0 flex-1">
          <button
            type="button"
            onClick={onOpen}
            disabled={barred}
            className="block text-start text-[13px] font-medium text-foreground underline
                       decoration-transparent underline-offset-2 hover:decoration-inherit
                       disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60
                       cursor-pointer"
          >
            {nameIn(locale, row.taskTypeName, row.taskTypeNameAr)}
          </button>

          <span className="mt-0.5 block text-[11.5px] text-muted-foreground">
            <bdi>{nameIn(locale, row.attendantName, row.attendantNameAr)}</bdi>
            {' · '}
            {fill(t.iqOnFloor, { floor: row.floorCode })}
            {row.submittedAtUtc && <>{' · '}{formatTime(new Date(row.submittedAtUtc), locale)}</>}
          </span>

          {/* **THE FIGURE THAT PREDICTS A REJECTION.** Drawn only when it is not zero: a
              row saying "0 exceptions" on every ordinary room buries the one with three. */}
          {row.exceptionCount > 0 && (
            <span className="mt-0.5 block text-[11.5px] font-medium"
                  style={{ color: 'var(--hk-hk-dirty-text)' }}>
              {pluralise(t.iqExceptions, row.exceptionCount, locale,
                formatCount(row.exceptionCount, locale))}
            </span>
          )}

          {barred && (
            <span className="mt-0.5 block text-[11.5px] text-muted-foreground">
              {t.iqCannot[row.cannotInspect!]}
            </span>
          )}
        </span>

        <span className={`shrink-0 tnum text-[12px] ${
          old ? 'font-semibold text-[#b45309] dark:text-[#fbbf24]' : 'text-muted-foreground'}`}
        >
          {formatMinutes(row.waitingMinutes, locale)}
        </span>
      </div>
    </article>
  )
}
